import { useState, useEffect } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export type SearchableSelectProps = {
  options: { label: string; value: string }[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}

// Maintain a reference to the previous value
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
}: SearchableSelectProps) => {
  // Initialize search with the label of the selected value
  const initialOption = options.find(opt => opt.value === value)
  const [search, setSearch] = useState(initialOption?.label || '')
  const [isOpen, setIsOpen] = useState(false)

  // Maintain a reference to the previous value
  const [prevValue, setPrevValue] = useState(value)

  // Only update `search` if `value` has changed and is valid
  useEffect(() => {
    if (prevValue !== value) {
      const selectedOption = options.find((opt) => opt.value === value)
      if (selectedOption) {
        setSearch(selectedOption.label)
      }
      setPrevValue(value) // Save new previous value
    }
  }, [value, options])

  // Filter options based on the entered text
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  )

  // Handle blur to validate the entered option and update the value if valid
  const handleBlur = () => {
    setTimeout(() => {
      const selectedOption = options.find(
        (opt) => opt.label.toLowerCase() === search.toLowerCase()
      )

      if (selectedOption) {
        onChange(selectedOption.value) // Autocomplete if valid
        setSearch(selectedOption.label)
      } else if (search !== '') {
        onChange('')
      } else {
        onChange('')
      }

      setIsOpen(false)
    }, 200) // Timeout prevents blur from closing the menu before selecting
  }

  return (
    <div className="relative w-full">
      {/* Input to write and filter */}
      <div
        className={`flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm shadow-sm`}
      >
        <input
          type="text"
          className="w-full bg-transparent outline-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder || 'Search...'}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          disabled={disabled}
        />

        {/* Button to clear */}
        {search && (
          <button
            onClick={() => {
              setSearch('')
              onChange('')
            }}
            className="ml-2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-dark-border"
          >
            <X className="h-4 w-4 text-gray-500 dark:text-dark-text-muted" />
          </button>
        )}

        <ChevronDown
          className="h-4 w-4 opacity-50 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>

      {/* List of options */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white dark:bg-dark-card dark:border-dark-border shadow-md max-h-48 overflow-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt.value}
                onMouseDown={() => {
                  // Prevent blur from closing the menu before selecting
                  onChange(opt.value)
                  setSearch(opt.label)
                  setIsOpen(false)
                }}
                className={cn(
                  'cursor-pointer select-none p-2 text-sm hover:bg-gray-100 dark:hover:bg-dark-accent'
                )}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="p-2 text-sm text-muted-foreground">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchableSelect
